import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Container, Grid, Alert, Typography, Button, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ProductCard from './ProductCard';
import { clearSelectedBrand, showBrands, selectPromotion } from '../../store/slices/productsSlice';
import { clearPromotionContext } from '../../store/slices/promotionSlice';
import { useLocalData } from '../../hooks/useLocalData';

/**
 * Componente para exibição de produtos de uma marca específica.
 * Baseado no componente ProductCategory.jsx, mas focado em mostrar produtos filtrados por marca.
 * Inclui botões de voltar (ícone no canto superior direito e botão no final).
 * Mobile: 2 cards por linha, Desktop: 4 cards por linha.
 * 
 * @component
 * @returns {JSX.Element} Container responsivo com grid de produtos da marca e controles de navegação
 */
export default function ProductBrand() {
  const dispatch = useDispatch();
  const { selectedBrand } = useSelector(state => state.products);
  const { promotionContext } = useSelector(state => state.promotion);
  const { getProductsByBrand } = useLocalData();

  const displayProducts = useMemo(() => {
    if (!selectedBrand) return [];
    return getProductsByBrand(selectedBrand.name);
  }, [selectedBrand, getProductsByBrand]);

  const maxLength = useMemo(
    () => (displayProducts.length === 1 ? { xs: '100%', sm: '80%', md: '50%', lg: '25%' } : '100%'),
    [displayProducts.length]
  );

  const handleGoBack = () => {
    if (promotionContext) {
      dispatch(selectPromotion(promotionContext));
      dispatch(clearPromotionContext());
    } else {
      dispatch(clearSelectedBrand());
      dispatch(showBrands());
    }
  };

  // Loading e erro são gerenciados pelo AppInitializer via contexto global

  return (
    <Box component='main' sx={{ pt: 8, pb: 6, minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{
          py: 4,
          mt: { xs: 5, sm: 5, md: '100px' },
          px: { xs: 1, sm: 3, md: 3 },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: { xs: 64, md: 80 },
            zIndex: 100,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: { xs: 8, sm: 8, md: 8 },
          }}
        >
          <Typography
            variant='h5'
            component='h5'
            sx={{
              textAlign: 'left',
              fontWeight: 'bold',
            }}
          >
            {selectedBrand ? `Produtos - ${selectedBrand.name}` : 'Produtos da Marca'}
          </Typography>
          
          <IconButton
            onClick={handleGoBack}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
            aria-label="Voltar"
          >
            <ArrowBack />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Grid
            container
            spacing={{ xs: 1, sm: 3, md: 3 }}
            sx={{
              maxWidth: { xs: '100%', sm: 1000, md: 1200 },
              width: '100%',
              margin: 0,
              mt: { xs: 1, md: 1 },
            }}
          >
            {displayProducts.length > 0 ? (
              displayProducts.map(product => (
                <Grid
                  key={product.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0,
                    maxWidth: { maxLength },
                  }}
                >
                  <ProductCard
                    id={product.id}
                    nome={product.nome}
                    marca={product.marca}
                    preco={product.preco}
                    imagem={product.imagem}
                    categoria={product.categoria}
                    descricao={product.descricao}
                  />
                </Grid>
              ))
            ) : (
              <Grid size={12}>
                <Alert severity='info'>
                  {selectedBrand 
                    ? `Nenhum produto encontrado da marca '${selectedBrand.name}'`
                    : 'Nenhum produto encontrado desta marca'
                  }
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={handleGoBack}
            sx={{
              px: 4,
              py: 1.5,
            }}
          >
            Voltar
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
